/* eslint-disable react/prop-types */
import React, {
  useContext, createContext, useEffect, useState
} from 'react';
import { clearLocalData, getLocalData, storeLocalData } from 'src/utils/getInitials';
import { useHistory } from 'react-router-dom';
import { APIService, setAPIAuthHeader } from 'src/api/api';
import { RESET_STORE, STORAGE_KEY, USER_ROLES } from 'src/utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { getAppSettings } from 'src/redux/settingSlice';

const authContext = createContext();

function useProvideAuth() {
  const history = useHistory();
  const dispatch = useDispatch();
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(Boolean(getLocalData(STORAGE_KEY.token, false, true)));
  const { appSettings, appSettingsLoaded } = useSelector((state) => state.settings);

  const signin = (data) => {
    const userData = {
      ...data.user,
      role: data.role,
      companies: data.companies || [],
      permissions: data.permissions || []
    };
    setUser(userData);
    setIsAuthenticated(true);
    storeLocalData(STORAGE_KEY.user, userData);
    setAPIAuthHeader(data.token);
    storeLocalData(STORAGE_KEY.token, data.token, true);
  };

  useEffect(() => {
    if (isAuthenticated && !appSettingsLoaded) {
      const currentUser = user || getLocalData(STORAGE_KEY.user, true);
      // Support users select a company in the support portal, so their unit
      // comes from the selected-company payload rather than global settings.
      if (currentUser?.role === USER_ROLES.support) return;

      const token = getLocalData(STORAGE_KEY.token, false, true);
      if (token) {
        setAPIAuthHeader(token);
        dispatch(getAppSettings());
      }
    }
  }, [appSettingsLoaded, dispatch, isAuthenticated, user]);

  useEffect(() => {
    if (!appSettings || !Object.prototype.hasOwnProperty.call(appSettings, 'fuel_volume_unit')) return;

    const currentUser = user || getLocalData(STORAGE_KEY.user, true);
    if (!currentUser) return;

    if (JSON.stringify(currentUser.fuel_volume_unit || null)
      === JSON.stringify(appSettings.fuel_volume_unit || null)) return;

    const updatedUser = {
      ...currentUser,
      fuel_volume_unit: appSettings.fuel_volume_unit
    };
    setUser(updatedUser);
    storeLocalData(STORAGE_KEY.user, updatedUser);
  }, [appSettings, user]);

  const isAuth = () => {
    const token = getLocalData(STORAGE_KEY.token, false, true);
    if (token) {
      setAPIAuthHeader(token);
    }
    return token;
  };

  const getUser = () => {
    let userInfo = user;
    if (!user) {
      userInfo = getLocalData(STORAGE_KEY.user, true);
    }
    return userInfo;
  };

  const signout = async () => {
    try {
      const body = { is_mobile: false };
      await APIService.userLogout(body);
      setUser(null);
      setIsAuthenticated(false);
      clearLocalData();
      dispatch({ type: RESET_STORE });
      history.replace('login');
    } catch (error) {
      console.log('logout error');
    }
  };

  return {
    getUser,
    isAuth,
    signin,
    signout
  };
}

export function AuthProvider({ children }) {
  const auth = useProvideAuth();
  return <authContext.Provider value={auth}>{children}</authContext.Provider>;
}

export default function useAuth() {
  return useContext(authContext);
}
